import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Compass, LayoutDashboard, Wrench, X } from 'lucide-react';
import { useViewModeStore, ViewMode } from '../../store/viewModeStore';
import { portalApi } from '../../services/portal';
import styles from './ViewModeShell.module.css';

/**
 * MV5 Sprint 2.D — the layout wrapper that swaps composition by view mode.
 *
 * Each learning surface (LearningItemPage today; the lab IDE tomorrow) wraps
 * its content in a ViewModeShell. The shell decides how to arrange the:
 *
 *   • `main` slot   — the primary surface (video, reader, quiz, IDE).
 *   • `aiPane` slot — proactive suggestions + Peek Player chip. Rendered
 *                     only in Standard mode; hidden in Guided/Studio.
 *   • `footer` slot — Mini-IDE chip + other bottom-row affordances.
 *   • `pip` slot    — the lecture PIP (Studio only).
 *
 * Guided mode:  main only, centered. Companion dock handles AI on its own
 *               (via the Layout).
 * Standard:     main + aiPane side-by-side; footer under main.
 * Studio:       IDE fills; lecture PIP in the corner. First-Studio coach-
 *               mark banner appears at the top the very first time.
 *
 * The mode toggle lives in the shell's header row (Settings-style chip trio)
 * so a learner can flip modes without leaving the lecture. Changes hit
 * PATCH /users/me/preferences in the background and fall back to
 * localStorage-only if the server is offline.
 */

export interface ViewModeShellProps {
  main: React.ReactNode;
  aiPane?: React.ReactNode;
  footer?: React.ReactNode;
  /** The lecture PIP shown in Studio mode. Optional; only rendered when Studio + supplied. */
  pip?: React.ReactNode;
  /** Hide the mode chips (e.g. for the IDE surface which has its own header row). */
  hideModeSwitcher?: boolean;
  className?: string;
}

const MODE_META: Record<ViewMode, { label: string; Icon: React.ComponentType<{ className?: string }> }> = {
  guided: { label: 'Guided', Icon: Compass },
  standard: { label: 'Standard', Icon: LayoutDashboard },
  studio: { label: 'Studio', Icon: Wrench },
};

const MODES: ViewMode[] = ['guided', 'standard', 'studio'];

const ViewModeShell: React.FC<ViewModeShellProps> = ({
  main,
  aiPane,
  footer,
  pip,
  hideModeSwitcher = false,
  className,
}) => {
  const { t } = useTranslation();
  const viewMode = useViewModeStore((s) => s.viewMode);
  const setViewMode = useViewModeStore((s) => s.setViewMode);
  const firstStudioAt = useViewModeStore((s) => s.firstStudioAt);
  const studioCoachDismissed = useViewModeStore((s) => s.studioCoachDismissed);
  const dismissStudioCoach = useViewModeStore((s) => s.dismissStudioCoach);

  const handleModeChange = (next: ViewMode) => {
    setViewMode(next);
    // Fire-and-forget server sync; local state already updated. If the server
    // 4xx/5xxs we still respect the client-side choice for this session.
    void portalApi.updatePreferences({ viewMode: next }).catch(() => undefined);
  };

  const modeSwitcher = !hideModeSwitcher && (
    <div className={styles.settingsRow} role="radiogroup" aria-label="View mode">
      {MODES.map((m) => {
        const { label, Icon } = MODE_META[m];
        const active = viewMode === m;
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={active}
            className={`${styles.modeChip} ${active ? styles.modeChipActive : ''}`}
            onClick={() => handleModeChange(m)}
          >
            <Icon className="size-3" />
            {label}
          </button>
        );
      })}
    </div>
  );

  const studioCoach = viewMode === 'studio'
    && firstStudioAt === null
    && !studioCoachDismissed && (
      <div className={styles.studioBanner} role="status">
        <Wrench className="size-4" />
        <span>
          Welcome to Studio. Your CPU sandbox is ready — <strong>GPU coming Wave 3</strong>.
          Drag the corner PIP to reposition the lecture.
        </span>
        <button
          type="button"
          onClick={dismissStudioCoach}
          aria-label="Dismiss Studio coach-mark"
        >
          <X className="size-4" />
        </button>
      </div>
  );

  if (viewMode === 'guided') {
    return (
      <div className={`${styles.shell} ${styles.guided} ${className ?? ''}`}>
        {modeSwitcher}
        {main}
        {footer && <div className={styles.footerRow}>{footer}</div>}
      </div>
    );
  }

  if (viewMode === 'standard') {
    return (
      <div className={`${styles.shell} ${className ?? ''}`}>
        {modeSwitcher}
        <div className={styles.standard}>
          <div className={styles.standardMain}>
            {main}
            {footer && <div className={styles.footerRow}>{footer}</div>}
          </div>
          <aside className={styles.standardAside} aria-label={t('ako.pane_aria', { name: t('companion.name') })}>
            <p className={styles.aiPaneHeader}>{t('companion.name')}</p>
            <div className={styles.aiPaneBody}>
              {aiPane ?? (
                <StandardAiPaneFallback />
              )}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // studio
  return (
    <div className={`${styles.shell} ${styles.studio} ${className ?? ''}`}>
      {modeSwitcher}
      {studioCoach}
      <div className={styles.studioIde}>{main}</div>
      {pip && <div className={styles.studioLecturePip}>{pip}</div>}
    </div>
  );
};

/**
 * Fallback content shown in Standard mode's AI pane when no proactive
 * suggestions have been supplied. Mirrors the Companion tone from the
 * design mocks — helpful but not intrusive.
 */
const StandardAiPaneFallback: React.FC = () => (
  <>
    <div className={styles.suggestion}>
      <p className={styles.suggestionKind}>Nudge</p>
      <p>
        I&rsquo;ll flag confusing moments as you watch. Ask me anything about
        this lecture — I have the transcript loaded.
      </p>
    </div>
    <div className={styles.suggestion}>
      <p className={styles.suggestionKind}>Try</p>
      <p>
        The mini-IDE chip in the footer opens a small editor so you can try
        the snippet without leaving this page.
      </p>
    </div>
  </>
);

/**
 * Bootstrap hook — mount somewhere at the app root (e.g. the Layout) to sync
 * the local viewMode store with the server-side preference on load. Also
 * saves the initial value if the server had a different one.
 */
export function useViewModeBootstrap() {
  const [ready, setReady] = useState(false);
  const hydrate = useViewModeStore((s) => s.hydrateFromServer);
  useEffect(() => {
    let cancelled = false;
    portalApi
      .getPreferences()
      .then((prefs) => {
        if (cancelled) return;
        hydrate(prefs.viewMode, prefs.firstStudioAt);
        setReady(true);
      })
      .catch(() => {
        // Offline / unauthenticated / server down — the local value remains
        // the source of truth. Return "ready" so the app renders.
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [hydrate]);
  return ready;
}

export default ViewModeShell;
